import type { TelemetryBrowserInstance } from 'telemetry/browser'
import { logger } from 'utils'

let telemetryInstance: TelemetryBrowserInstance | null = null
let isInitialized = false

type TelemetryContext = {
    organizationSlug?: string
    agentId?: string
    userId?: string
}

let telemetryContext: TelemetryContext = {}

function mergedContext(
    properties?: Record<string, string>,
): Record<string, string> {
    const base: Record<string, string> = {
        source: 'web-component',
        hostOrigin: window.location.origin,
        hostUrl: window.location.href,
    }

    if (telemetryContext.organizationSlug) {
        base.organizationSlug = telemetryContext.organizationSlug
    }
    if (telemetryContext.agentId) {
        base.agentId = telemetryContext.agentId
    }
    if (telemetryContext.userId) {
        base.userId = telemetryContext.userId
    }

    return {
        ...base,
        ...(properties ?? {}),
    }
}

/**
 * Initializes OpenTelemetry for the web component widget.
 *
 * The SDK is loaded through a dynamic `import()`, so it never blocks the
 * first render: the browser build of OpenTelemetry weighs about 37 kB
 * gzipped, and the widget is served from a CDN to pages that are not ours.
 * Nothing waits for it. What it collects is automatic instrumentation
 * (document load, fetch), so a call made before the chunk lands is simply
 * not traced; `trackAppEvent` and the rest write to the logger and do not
 * read the instance at all.
 *
 * Uses a singleton pattern: only the first call with a valid configuration
 * takes effect. Subsequent calls are no-ops, and so is every call when
 * `VITE_OTEL_ENABLED` is not true, which is what keeps the chunk from being
 * fetched at all.
 */
export function initializeAppInsights(connectionString?: string): void {
    void connectionString

    if (isInitialized) {
        return
    }

    const isEnabled =
        import.meta.env.VITE_OTEL_ENABLED?.toLowerCase() === 'true'
    if (!isEnabled) {
        return
    }

    isInitialized = true

    const rawSampling = import.meta.env.VITE_OTEL_SAMPLING_RATE
    const samplingRate = rawSampling ? Number(rawSampling) : 1

    void import('telemetry/browser')
        .then(({ initializeTelemetryBrowser }) => {
            telemetryInstance = initializeTelemetryBrowser({
                serviceName:
                    import.meta.env.VITE_OTEL_SERVICE_NAME ?? 'web-component',
                environment:
                    import.meta.env.VITE_OTEL_ENVIRONMENT ?? 'development',
                // Derived from the backend URL: the OTLP exporter targets the
                // backend's /api/otel proxy, so it is not configured separately.
                exporterEndpoint: import.meta.env.VITE_BACKEND_URL
                    ? `${import.meta.env.VITE_BACKEND_URL}/api/otel`
                    : undefined,
                samplingRate: Number.isFinite(samplingRate)
                    ? Math.max(0, Math.min(1, samplingRate))
                    : 1,
            })

            window.addEventListener('beforeunload', async () => {
                await telemetryInstance?.flush()
                await telemetryInstance?.shutdown()
            })

            logger.info(
                '[telemetry] web-component initialized',
                mergedContext(),
            )
        })
        .catch((caught) => {
            // The host page keeps working without telemetry, so this is worth
            // a line in the console and nothing more.
            logger.warn(
                '[telemetry] the browser SDK could not be loaded',
                caught,
            )
        })
}

/**
 * Updates telemetry context enriched onto every subsequent event.
 */
export function setTelemetryContext(ctx: Partial<TelemetryContext>): void {
    telemetryContext = { ...telemetryContext, ...ctx }
}

/**
 * Tracks a custom event when telemetry is initialized.
 */
export function trackAppEvent(
    name: string,
    properties?: Record<string, string>,
): void {
    logger.debug('[telemetry:event]', name, mergedContext(properties))
}

/**
 * Tracks a custom metric when telemetry is initialized.
 */
export function trackAppMetric(
    name: string,
    average: number,
    properties?: Record<string, string>,
): void {
    logger.debug('[telemetry:metric]', name, average, mergedContext(properties))
}

/**
 * Tracks an exception when telemetry is initialized.
 */
export function trackAppException(
    error: Error,
    properties?: Record<string, string>,
): void {
    logger.error(error, mergedContext(properties))
}
